/**
 * Addon Item Single Module
 * ========================
 * Handles single addon product (frame-addon.js refactored)
 * - Checkbox management
 * - Variant selection
 * - Price updates
 * - Cart integration
 */

(function() {
  'use strict';

  /**
   * AddonItemSingle
   * Manages single addon with variant selection
   */
  class AddonItemSingle {
    constructor(element) {
      this.container = element;
      this.checkbox = this.container.querySelector('[data-addon-single-checkbox]');
      this.framePrice = parseInt(this.container.dataset.framePrice);
      this.frameVariantId = this.container.dataset.frameVariantId;
      this.variantCount = parseInt(this.container.dataset.variantCount) || 1;
      this.frameTitle = this.container.dataset.frameTitle || 'Addon';

      // Variant selector elements
      this.variantSelector = this.container.querySelector('[data-addon-variant-selector]');
      this.variantButtons = this.container.querySelectorAll('[data-addon-variant-button]');
      this.selectedEditionDisplay = this.container.querySelector('[data-addon-selected-edition]');

      // Order summary elements (2026-01-17)
      this.orderSummary = this.container.querySelector('[data-addon-order-summary]');
      this.summaryPrompt = this.container.querySelector('[data-summary-prompt]');
      this.summaryDetails = this.container.querySelector('[data-summary-details]');
      this.summaryDetailsSingle = this.container.querySelector('[data-summary-details-single]');
      this.summaryMainName = this.container.querySelector('[data-summary-main-name]');
      this.summaryMainPrice = this.container.querySelector('[data-summary-main-price]');
      this.summaryGiftName = this.container.querySelector('[data-summary-gift-name]');
      this.summaryTotal = this.container.querySelector('[data-summary-total]');

      // Modal elements
      this.modalTrigger = this.container.querySelector('[data-addon-modal-trigger]');

      // State
      this.jerseyVariantId = null;
      this.jerseyPrice = 0;
      this.selectedFrameVariantId = this.frameVariantId;
      this.selectedFrameEdition = null;

      this.init();
    }

    /**
     * Initialize addon
     */
    init() {
      // Get initial jersey price
      this.initializeJerseyPrice();

      // Listen for variant selection
      document.addEventListener('variant:selected', this.handleVariantChange.bind(this));

      // Listen for checkbox changes
      if (this.checkbox) {
        this.checkbox.addEventListener('change', this.handleCheckboxChange.bind(this));
      }

      // Listen for variant button clicks
      this.variantButtons.forEach(button => {
        button.addEventListener('click', this.handleVariantClick.bind(this));
      });

      // Setup form intercept
      if (this.checkbox) {
        this.setupFormIntercept();
      }

      // Setup modal
      this.setupModal();
    }

    /**
     * Initialize jersey price from current variant
     */
    initializeJerseyPrice() {
      const productData = AddonUtilities.getProductData();
      if (!productData) return;

      const variantId = AddonUtilities.getSelectedVariantId();
      let variant = null;

      if (variantId) {
        variant = productData.variants.find(v => v.id.toString() === variantId.toString());
      }

      // Fallback to first available
      if (!variant) {
        variant = productData.variants.find(v => v.available) || productData.variants[0];
      }

      if (variant) {
        this.jerseyVariantId = variant.id;
        this.jerseyPrice = variant.price;
      }
    }

    /**
     * Handle variant selection from page
     */
    handleVariantChange(event) {
      const { variantId } = event.detail;
      const productData = AddonUtilities.getProductData();

      if (!productData) return;

      const variant = productData.variants.find(v => v.id.toString() === variantId.toString());
      if (!variant) return;

      this.jerseyVariantId = variant.id;
      this.jerseyPrice = variant.price;

      // Update order summary with new jersey selection
      this.updateOrderSummary();
    }

    /**
     * Handle checkbox change
     */
    handleCheckboxChange() {
      const isChecked = this.checkbox.checked;

      // Show/hide variant selector (only if multiple variants)
      if (this.variantSelector && this.variantCount > 1) {
        this.variantSelector.style.display = isChecked ? 'block' : 'none';
      }

      // For single-variant products, auto-select the variant
      if (isChecked && this.variantCount <= 1) {
        this.selectedFrameVariantId = this.frameVariantId;
        this.selectedFrameEdition = null; // No edition number for single variants
      }

      // Update order summary state (2026-01-17)
      this.updateOrderSummaryState(isChecked);
    }

    /**
     * Update order summary display state
     * Shows prompt when addon not selected, shows details when selected
     */
    updateOrderSummaryState(isAddonChecked) {
      if (!this.orderSummary) return;

      // Single-variant: hide entire summary container when unchecked
      if (this.variantCount <= 1) {
        this.orderSummary.style.display = isAddonChecked ? 'block' : 'none';
        if (this.summaryDetailsSingle) {
          this.summaryDetailsSingle.style.display = isAddonChecked ? 'flex' : 'none';
        }
        return;
      }

      // Multi-variant: toggle between prompt and details
      if (isAddonChecked) {
        if (this.summaryPrompt) this.summaryPrompt.style.display = 'none';
        if (this.summaryDetails) this.summaryDetails.style.display = 'flex';
        this.updateOrderSummary();
      } else {
        if (this.summaryPrompt) this.summaryPrompt.style.display = 'flex';
        if (this.summaryDetails) this.summaryDetails.style.display = 'none';
      }
    }

    /**
     * Update order summary with current selections
     */
    updateOrderSummary() {
      // Update jersey info
      if (this.summaryMainName) {
        const jerseyEdition = this.getJerseyEditionDisplay();
        this.summaryMainName.textContent = jerseyEdition || 'Select edition';
      }

      if (this.summaryMainPrice) {
        this.summaryMainPrice.textContent = this.formatMoney(this.jerseyPrice);
      }

      // Update gift info
      if (this.summaryGiftName) {
        // Single variant products - just show product title
        if (this.variantCount <= 1) {
          this.summaryGiftName.textContent = this.frameTitle;
        } else if (this.selectedFrameEdition) {
          this.summaryGiftName.textContent = `${this.frameTitle} #${this.selectedFrameEdition}/${this.variantCount}`;
        } else {
          this.summaryGiftName.textContent = 'Select edition above';
        }
      }

      // Update total (jersey price only, gift is free)
      if (this.summaryTotal) {
        this.summaryTotal.textContent = this.formatMoney(this.jerseyPrice);
      }
    }

    /**
     * Get jersey edition display text
     */
    getJerseyEditionDisplay() {
      // Try to get from the searchable picker's selected display
      const selectedDisplay = document.querySelector('.searchable-picker__selected');
      if (selectedDisplay) {
        const text = selectedDisplay.textContent.trim();
        if (text && text !== 'No number selected') {
          return text;
        }
      }
      return null;
    }

    /**
     * Format cents to money string
     */
    formatMoney(cents) {
      if (!cents || isNaN(cents)) return '$0.00';
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    }

    /**
     * Handle variant button click
     */
    handleVariantClick(event) {
      const button = event.currentTarget;
      const variantId = button.dataset.variantId;
      const editionNumber = button.dataset.editionNumber;

      this.selectedFrameVariantId = variantId;
      this.selectedFrameEdition = editionNumber;

      // Update button states
      this.variantButtons.forEach(btn => {
        btn.setAttribute('aria-pressed', 'false');
      });
      button.setAttribute('aria-pressed', 'true');

      // Update display
      if (this.selectedEditionDisplay) {
        this.selectedEditionDisplay.innerHTML = `Selected: <strong>Limited Edition #${editionNumber}/${this.variantCount}</strong>`;
      }

      // Update order summary with new selection
      this.updateOrderSummary();
    }

    /**
     * Setup modal open/close
     * Uses the shared AddonModalController from addon-item-core.js
     */
    setupModal() {
      if (!this.modalTrigger) return;

      this.modalTrigger.addEventListener('click', async (event) => {
        event.preventDefault();

        // Get the modal controller singleton
        const modalController = window.addonModal || window.AddonModalController?.getInstance();

        if (!modalController) {
          console.warn('AddonItemSingle: AddonModalController not available. Ensure addon-item-core.js is loaded.');
          return;
        }

        // Get product handle from data attribute or container
        const productHandle = this.modalTrigger.dataset.productHandle ||
                              this.container.dataset.productHandle ||
                              this.container.dataset.frameHandle;

        if (productHandle) {
          // Fetch product data and display in modal
          try {
            const productData = await AddonUtilities.fetchProduct(productHandle);
            if (productData && productData.product) {
              modalController.open(productData.product);
            } else if (productData) {
              modalController.open(productData);
            }
          } catch (error) {
            console.error('Failed to fetch product for modal:', error);
          }
        } else {
          // Fallback: try to get product data from the addon container's embedded data
          const addonProductData = this.getAddonProductData();
          if (addonProductData) {
            modalController.open(addonProductData);
          }
        }
      });
    }

    /**
     * Get addon product data from container dataset or embedded JSON
     * @returns {Object|null} Product data
     */
    getAddonProductData() {
      // Try to get from embedded script tag
      const productScript = this.container.querySelector('script[type="application/json"][data-addon-product-json]');
      if (productScript) {
        try {
          return JSON.parse(productScript.textContent);
        } catch (e) {
          console.error('Failed to parse addon product JSON:', e);
        }
      }

      // Fallback: construct from data attributes
      const frameTitle = this.container.dataset.frameTitle;
      const frameImage = this.container.dataset.frameImage;
      const frameUrl = this.container.dataset.frameUrl;
      const framePrice = parseInt(this.container.dataset.framePrice) || 0;
      const frameCompareAtPrice = parseInt(this.container.dataset.frameCompareAtPrice) || 0;

      if (frameTitle) {
        return {
          title: frameTitle,
          featured_image: frameImage,
          url: frameUrl,
          price: framePrice,
          compare_at_price: frameCompareAtPrice
        };
      }

      return null;
    }

    /**
     * Setup form submission intercept
     */
    setupFormIntercept() {
      const productForm = document.querySelector('.js-product-form-main');
      if (!productForm) return;

      const submitButton = productForm.querySelector('[name="add"]');
      if (!submitButton) return;

      submitButton.addEventListener('click', async (event) => {
        if (!this.checkbox || !this.checkbox.checked) {
          return; // Let default behavior happen
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        if (!this.jerseyVariantId) {
          alert('Please select a jersey variant first');
          return;
        }

        // Only require edition selection if product has multiple variants
        if (!this.selectedFrameVariantId && this.variantCount > 1) {
          alert('Please select a frame edition number');
          return;
        }

        // For single-variant products, ensure we have the variant ID
        if (!this.selectedFrameVariantId) {
          this.selectedFrameVariantId = this.frameVariantId;
        }

        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('is-loading');

        try {
          await this.addBothToCart();

          submitButton.classList.remove('is-loading');
          submitButton.classList.add('is-success');

          // Refresh cart
          AddonUtilities.refreshCartDrawer();

          setTimeout(() => {
            AddonUtilities.openCartDrawer();
            submitButton.classList.remove('is-success');
            submitButton.setAttribute('aria-disabled', 'false');
          }, 300);
        } catch (error) {
          console.error('Failed to add to cart:', error);
          alert('Failed to add items to cart. Please try again.');

          submitButton.classList.remove('is-loading');
          submitButton.setAttribute('aria-disabled', 'false');
        }
      }, true);

      // Also intercept form submit
      productForm.addEventListener('submit', (event) => {
        if (this.checkbox && this.checkbox.checked) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);
    }

    /**
     * Add both jersey and frame to cart
     */
    async addBothToCart() {
      const bundleId = Date.now().toString();

      const items = [
        {
          id: parseInt(this.jerseyVariantId),
          quantity: 1,
          properties: {
            '_bundle_id': bundleId
          }
        },
        {
          id: parseInt(this.selectedFrameVariantId),
          quantity: 1,
          properties: {
            '_promo': 'Limited Drop - Free Frame',
            '_auto_added': 'true',
            '_free_addon': 'true',
            '_bundle_id': bundleId,
            'Bundle Deal': 'FREE with Jersey Purchase'
          }
        }
      ];

      return await AddonUtilities.addToCart(items);
    }
  }

  /**
   * Initialize all single addon instances
   */
  function initAddonSingle() {
    const addons = document.querySelectorAll('[data-addon-single]');
    addons.forEach(addon => {
      new AddonItemSingle(addon);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAddonSingle);
  } else {
    initAddonSingle();
  }

  // Export for external use
  if (typeof window !== 'undefined') {
    window.AddonItemSingle = AddonItemSingle;
  }
})();
